import axios, { AxiosInstance } from 'axios';
import camelcaseKeys from 'camelcase-keys';
import { convertKeysToSnakeCase } from 'libs/utils';

export const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_ENDPOINT,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 180000, // 3 minutes
});

export const handleApiRequest = async <T>(
  request: Promise<T>,
): Promise<{
  data: T | null;
  error: string | null;
  statusCode: number | null;
}> => {
  try {
    const response = await request;
    const data =
      (response as any).data !== undefined ? (response as any).data : response;
    const dataCamelCase = camelcaseKeys(data, { deep: true, preserveConsecutiveUppercase: true });
    return {
      data: dataCamelCase,
      error: null,
      statusCode: (response as any).status,
    };
  } catch (err) {
    let errorMessage = 'Unknown error';
    let statusCode = null;
    if (err instanceof Error) {
      errorMessage = err.message;
    }
    if (axios.isAxiosError(err)) {
      if (err.response?.data) {
        const errorCamelCase = camelcaseKeys(err.response.data, { deep: true, preserveConsecutiveUppercase: true });
        if (errorCamelCase.errorMessage) {
          errorMessage = errorCamelCase.errorMessage;
        } else if (errorCamelCase.message) {
          errorMessage = errorCamelCase.message;
        }
      }
      statusCode = err.response?.status || null;
    }
    return { data: null, error: errorMessage, statusCode };
  }
};

export const handleExportFileRequest = async (
  request: Promise<any>,
  fileName: string,
): Promise<{
  data: Record<string, any> | null;
  error: string | null;
  statusCode: number | null;
}> => {
  try {
    const response = await request;
    if (response.status !== 200) {
      const responseData = response.data;
      return { data: responseData, error: null, statusCode: response.status };
    }

    const res_url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = res_url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    return { data: { status: 'success' }, error: null, statusCode: response.status };
  } catch (error) {
    let errorMessage = 'An unknown error occurred';
    let statusCode = 500;
    if (axios.isAxiosError(error) && error.response) {
      errorMessage = error.response.data?.message || errorMessage;
      statusCode = error.response.status;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    return { data: null, error: errorMessage, statusCode };
  }
};

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    const contentType = config.headers['Content-Type'] || config.headers['content-type'];
    if (
      (config.method === 'post' || config.method === 'put') &&
      config.data
    ) {
      if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
        const newFormData = new FormData();
        for (const [key, value] of Array.from(config.data.entries())) {
          const snakeKey = convertKeysToSnakeCase({ [key]: null }, true);
          const snakeKeyStr = Object.keys(snakeKey)[0];
          newFormData.append(snakeKeyStr, value);
        }
        config.data = newFormData;
      } else if (!contentType || contentType === 'application/json') {
        config.data = convertKeysToSnakeCase(config.data, true);
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect if already on signin page
      if (window.location.pathname !== '/signin') {
        window.location.href = '/signin';
      }
    }
    return Promise.reject(error);
  },
);
